import { formatUSPhoneNumber } from "../../../../utils/formatters";

/**
 * Get the appropriate action label for user assignment
 * @param {Object} record - The record object containing user data
 * @returns {string} - "Reassign user" if user exists, "Assign user" otherwise
 */
export const getUserActionLabel = record => {
  return record.user ? "Reassign user" : "Assign user";
};

/**
 * Get the appropriate action label for department assignment
 * @param {Object} record - The record object containing departmentLevel data
 * @returns {string} - "Reassign department" if departmentLevel exists, "Assign department" otherwise
 */
export const getDepartmentActionLabel = record => {
  return record.departmentLevel ? "Reassign department" : "Assign department";
};

/**
 * Get the appropriate action label for extension assignment
 * @param {Object} record - The record object containing callExtensionId data
 * @returns {string} - "Reassign extension" if callExtensionId exists, "Assign extension" otherwise
 */
export const getExtensionActionLabel = record => {
  return record.callExtension?.id ? "Reassign extension" : "Assign extension";
};

/**
 * Get the appropriate action label for location assignment
 * @param {Object} record - The record object containing originalLocation data
 * @returns {string} - "Reassign location" if originalLocation exists, "Assign location" otherwise
 */
export const getLocationActionLabel = record => {
  return record?.originalLocation?.title ? "Reassign location" : "Assign location";
};

/**
 * Static list of departments for assignment
 * Each department has:
 * - id: uppercase value (e.g., "FINANCE")
 * - name: capitalized label (e.g., "Finance")
 */
export const STATIC_DEPARTMENTS = [
  {
    id: "FINANCE",
    name: "Finance",
  },
  {
    id: "MARKETING",
    name: "Marketing",
  },
  {
    id: "PARTS",
    name: "Parts",
  },
  {
    id: "SALES",
    name: "Sales",
  },
  {
    id: "SERVICE",
    name: "Service",
  },
  {
    id: "OTHER",
    name: "Other",
  },
  {
    id: "UNASSIGNED",
    name: "Unassigned",
  },
];

/**
 * Get item identifier for drawer header display
 * Extracts the appropriate identifier from phone, extension, user, or device data
 * @param {Object} phoneData - Phone number data
 * @param {Object} extensionData - Extension data
 * @param {Object} userData - User data
 * @param {Object} deviceData - Device data
 * @returns {string} - The item identifier (phone number, extension, name, etc.)
 */
export const getItemIdentifier = (phoneData, extensionData, userData, deviceData) => {
  return (
    (phoneData?.phoneNumber && formatUSPhoneNumber(phoneData?.phoneNumber)) ||
    extensionData?.number ||
    userData?.callUserLines?.[0]?.name ||
    deviceData?.name ||
    "Item"
  );
};

/**
 * Format department for table display
 * Converts department value to uppercase for consistent display
 * @param {string|null} department - The department value
 * @returns {string} - Formatted department name in uppercase or "--" if not provided
 */
export const formatDepartmentForTable = department => {
  if (!department) return "--";
  return department.toUpperCase();
};

/**
 * Get department filters for table columns
 * Creates filter options from STATIC_DEPARTMENTS array
 * @returns {Array} - Array of filter objects with text and value properties
 */
export const getDepartmentFilters = () => {
  return STATIC_DEPARTMENTS.map(dept => ({
    text: dept.name,
    value: dept.id,
  }));
};

/**
 * User assignment filters for EAI User column
 * Filter options for Unassigned and Assigned users
 */
export const USER_ASSIGNMENT_FILTERS = [
  { text: "Unassigned", value: "true" },
  { text: "Assigned", value: "false" },
];

/**
 * Filter Keys - Constants for table filter keys
 * Used to avoid hardcoded strings in filter configurations
 */
export const GOTO_FILTER_KEYS = {
  USER: "user",
  DEPARTMENT_LEVEL: "departmentLevel",
  DEPARTMENT: "department",
  IS_ACTIVE: "isActive",
  LOCATION: "originalLocationId",
};

/**
 * Filter Values - Constants for filter values
 */
export const GOTO_FILTER_VALUES = {
  TRUE: "true",
  FALSE: "false",
};

/**
 * Status Colors - Constants for status tag colors
 */
export const GOTO_STATUS_COLORS = {
  ACTIVE: "blue",
  INACTIVE: "red",
};

/**
 * Helper function to get location filters for table columns
 * @param {Array} locations - Array of location objects (optional)
 * @returns {Array} - Array of filter objects for Ant Design Table
 */
export const getLocationFilters = locations => {
  return (locations || []).map(location => ({
    text: location.title,
    value: location.id,
  }));
};

export const GOTO_ORIGINAL_LOCATION_TOOLTIP =
  "This column allows you to set the original location for GoTo integration. This is useful for ensuring that data from GoTo is correctly associated with the intended location in your system.";
